import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useLang } from "@/lib/i18n";
import Loader from "@/components/Loader";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip } from "recharts";
import { Users, CalendarCheck, FileText, Mail, MapPin, Lightbulb, BadgeIndianRupee, TrendingUp, Wallet, Clock, QrCode } from "lucide-react";

export default function AdminDashboard() {
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [conv, setConv] = useState(null);

  useEffect(() => {
    api.get("/admin/dashboard").then(({ data }) => setData(data));
    api.get("/admin/contacts").then(({ data }) => setContacts(data.contacts || []));
    api.get("/admin/paywall-conversion").then(({ data }) => setConv(data)).catch(() => {});
  }, []);

  if (!data) return <Loader />;
  const stats = [
    { icon: Users, label: "Total Users", value: data.total_users, c: "#2563EB" },
    { icon: BadgeIndianRupee, label: "Active Subscribers", value: data.active_subscribers ?? 0, c: "#0B6B3A" },
    { icon: Wallet, label: "Revenue (Month)", value: `₹${data.revenue_month ?? 0}`, c: "#059669" },
    { icon: TrendingUp, label: "Total Revenue", value: `₹${data.revenue_total ?? 0}`, c: "#7C3AED" },
    { icon: Clock, label: "Expiring ≤15d", value: data.expiring_soon ?? 0, c: "#EA580C" },
    { icon: QrCode, label: "QR Pending", value: data.qr_pending ?? 0, c: "#0891B2" },
    { icon: CalendarCheck, label: "Active Today", value: data.active_today, c: "#0B6B3A" },
    { icon: FileText, label: "Entries (Month)", value: data.entries_this_month, c: "#EA580C" },
    { icon: Mail, label: "Unread Messages", value: data.contacts_unread, c: "#DB2777" },
    { icon: Lightbulb, label: "New Suggestions", value: data.suggestions_unread, c: "#F97316" },
  ];

  return (
    <div className="space-y-5">
      <h1 className="text-2xl md:text-3xl font-heading font-extrabold">Admin {t("dashboard")}</h1>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {stats.map((s, i) => (
          <div key={i} className="card-thali p-4" data-testid={`admin-stat-${i}`}>
            <s.icon className="w-6 h-6 mb-2" style={{ color: s.c }} />
            <div className="text-xs text-thali-ink/60">{s.label}</div>
            <div className="font-num font-extrabold text-2xl">{s.value}</div>
          </div>
        ))}
      </div>

      {conv && (
        <div className="card-thali" data-testid="paywall-conversion-card">
          <div className="font-heading font-bold flex items-center gap-2 mb-3"><TrendingUp className="w-5 h-5 text-thali-green" /> Paywall Conversion (payments ÷ paywall shown)</div>
          <div className="grid grid-cols-3 gap-3 text-center">
            {[["Today", conv.today], ["7 days", conv.d7], ["30 days", conv.d30]].map(([label, w]) => (
              <div key={label} className="rounded-xl bg-thali-cream p-3">
                <div className="text-xs text-thali-ink/60">{label}</div>
                <div className="font-num font-extrabold text-2xl text-thali-green">{w.rate}%</div>
                <div className="text-[11px] text-thali-ink/50">{w.paid}/{w.shown} shown</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        <div className="card-thali">
          <h2 className="font-heading font-bold mb-3 flex items-center gap-2"><MapPin className="w-4 h-4" /> Users by District</h2>
          {data.by_district.length ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data.by_district} layout="vertical" margin={{ left: 10 }}>
                <XAxis type="number" hide /><YAxis type="category" dataKey="name" width={90} fontSize={11} />
                <Tooltip cursor={{ fill: "#FFF8EC" }} />
                <Bar dataKey="count" fill="#0B6B3A" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-sm text-thali-ink/50">No data yet.</p>}
        </div>
        <div className="card-thali">
          <h2 className="font-heading font-bold mb-3 flex items-center gap-2"><MapPin className="w-4 h-4" /> Users by Block</h2>
          {data.by_block.length ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data.by_block} layout="vertical" margin={{ left: 10 }}>
                <XAxis type="number" hide /><YAxis type="category" dataKey="name" width={90} fontSize={11} />
                <Tooltip cursor={{ fill: "#FFF8EC" }} />
                <Bar dataKey="count" fill="#F97316" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-sm text-thali-ink/50">No data yet.</p>}
        </div>
      </div>

      <div className="card-thali">
        <h2 className="font-heading font-bold mb-3 flex items-center gap-2"><Mail className="w-4 h-4" /> Contact Messages</h2>
        <div className="space-y-2 max-h-80 overflow-y-auto">
          {contacts.length ? contacts.map((c) => (
            <div key={c.id} className="rounded-xl bg-thali-cream p-3" data-testid={`admin-contact-${c.id}`}>
              <div className="flex justify-between text-sm font-semibold"><span>{c.name}</span><span className="text-thali-ink/50">{c.mobile} {c.email}</span></div>
              <p className="text-sm text-thali-ink/70 mt-1">{c.message}</p>
            </div>
          )) : <p className="text-sm text-thali-ink/50">No messages.</p>}
        </div>
      </div>
    </div>
  );
}
